import { AxiosError } from 'axios';
import { IconLock } from '@tabler/icons-react';

import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { Alert, Flex, Group, Paper, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';

import { AzureDevOpsFileContentDto, DocumentDto } from '../../api/models';
import { useGetApiProjectsId } from '../../api/projects/projects.ts';
import { customInstance } from '../../../orvalCustomAxiosInstance';
import { LoadingFallBack } from '../utils/LoadingFallBack.tsx';
import ServerLoadErrorFallback from '../utils/ServerLoadErrorFallback.tsx';
import MilkdownEditorComponent from './milkdown/MilkdownEditor';
import { MilkdownAdapter } from './milkdown/milkdown-adapter';

const DevOpsEditorView = () => {
    const { projectId, '*': filePath } = useParams();
    const decodedFilePath = filePath ? decodeURIComponent(filePath) : '';

    const { data: project, isLoading: loadingProject, error: projectError } = useGetApiProjectsId(projectId ?? '');

    const { data: fileData, isLoading: loadingFile, error: fileError } = useQuery({
        queryKey: [`/api/AzureDevOps/project/${projectId}/file`, { path: decodedFilePath }],
        queryFn: ({ signal }) =>
            customInstance<AzureDevOpsFileContentDto>({
                url: `/api/AzureDevOps/project/${projectId}/file`,
                method: 'GET',
                params: { path: decodedFilePath },
                signal
            }),
        enabled: !!(projectId && decodedFilePath && project)
    });

    const adapter = useMemo(() => new MilkdownAdapter(), []);
    useEffect(() => () => adapter.destroy(), [adapter]);

    const sanitizedContent = fileData?.content ?? '';

    // Build a pseudo DocumentDto for the editor
    const pseudoDocument: DocumentDto | undefined = fileData ? {
        id: `devops-${projectId}-${decodedFilePath}`,
        content: sanitizedContent,
        name: decodedFilePath.split('/').pop() ?? decodedFilePath,
        title: decodedFilePath.split('/').pop()?.replace('.md', '') ?? '',
        documentCode: '',
        documentNumber: '',
        deleted: false,
        lastUpdated: new Date().toISOString(),
        originalAuthor: { id: '', displayName: '', emailAddress: '' },
        projectId: projectId ?? '',
        template: 'BlankTemplate',
        titlePage: false,
        notionPhaseId: null,
        markdownTemplate: null
    } : undefined;

    const isLoading = loadingProject || loadingFile;
    const error = projectError ?? fileError;

    if (isLoading || !pseudoDocument) return <LoadingFallBack message={'Loading file'} wrapInMaxWidth />;
    if (error) return <ServerLoadErrorFallback entity={'DevOps File'} error={error as AxiosError} />;

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const noop = async () => {};

    return (
        <Flex
            justify="stretch"
            direction="column"
            miw="80"
            style={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}
        >
            <Paper shadow="xl" radius="xs" withBorder style={{ height: '100%' }}>
                <Alert
                    icon={<IconLock size={16} />}
                    color="secondary"
                    variant="light"
                    py="xs"
                    radius={0}
                    styles={{ root: { borderBottom: '1px solid var(--mantine-color-default-border)' } }}
                >
                    <Group gap="xs">
                        <Text size="sm" fw={500}>{decodedFilePath}</Text>
                        <Text size="xs" c="dimmed">Read-only — sourced from repository</Text>
                    </Group>
                </Alert>
                <MilkdownEditorComponent
                    adapter={adapter}
                    data={pseudoDocument}
                    readOnly
                    saveDoc={noop}
                    exportDocX={noop}
                    openBpmnGenerator={noop}
                />
            </Paper>
        </Flex>
    );
};

export default DevOpsEditorView;
